import { LoadingButton } from "@mui/lab";
import {
	Alert,
	AlertTitle,
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	Grid,
	IconButton,
	InputAdornment,
	InputLabel,
	List,
	ListItem,
	ListItemText,
	MenuItem,
	Select,
	TextField,
	Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import WatchLaterIcon from "@mui/icons-material/WatchLater";
import ModeCommentIcon from "@mui/icons-material/ModeComment";
import StickyNote2Icon from "@mui/icons-material/StickyNote2";
import { GlobalContext } from "../../../GlobalContext";
import { Context } from "../../table/overtime/Context";
import { applyingOvertime } from "../../../utils/Overtime";
import { trackGaClick } from "../../../utils/analytics";

const Approve = ({ open, setOpen, data, checked }) => {
	const { t } = useTranslation("overtime.form.approve");

	const { reloading, comments, loadingComments } = useContext(Context);

	const { handlerAlert } = useContext(GlobalContext);

	const [loading, setLoading] = useState(false);
	const [overtimes, setOvertimes] = useState([]);
	const [employeeComments, setEmployeeComments] = useState({});
	const [employeeNotes, setEmployeeNotes] = useState({});
	const [openCommentDialog, setOpenCommentDialog] = useState(false);
	const [openNoteDialog, setOpenNoteDialog] = useState(false);
	const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);

	const overtimeRequiresComment = JSON.parse(sessionStorage.getItem("privileges"))?.overtime?.overtime_requires_coment || false;

	const closing = () => {
		setOpen(false);
		setOvertimes([]);
		setLoading(false);
		setEmployeeComments({});
		setEmployeeNotes({});
		setOpenCommentDialog(false);
		setOpenNoteDialog(false);
		setSelectedEmployeeId(null);
	};

	useEffect(() => {
		if (open) {
			setOvertimes(data.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending"));
		}
	}, [checked, data, open]);

	return (
		<>
			<Dialog
				fullWidth
				open={open}
				onClose={() => {
					closing();
				}}
			>
				<DialogTitle>
					<Typography variant="body3">{t("approve.title")}</Typography>
				</DialogTitle>

				{data.filter((d) => checked.includes(d.id)).some((d) => d.status !== "pending") && (
					<DialogContent
						sx={{
							backgroundColor: (theme) => theme.palette.background.default,
							borderBottom: "none",
						}}
						dividers
					>
						<Grid container spacing={2}>
							<Grid
								item
								xs={12}
								sx={{
									height: "100%",
								}}
							>
								<Alert
									severity="warning"
									sx={{
										width: "100%",
										opacity: 1,
									}}
									variant="filled"
								>
									<AlertTitle>{t("approve.alerts.already_managed.title")}</AlertTitle>

									{t("approve.alerts.already_managed.message")}
								</Alert>
							</Grid>
						</Grid>
					</DialogContent>
				)}

				<DialogContent dividers>
					<Typography variant="body3">
						{t("approve.message")}

						{overtimes.length !== 0 && (
							<List>
								{overtimes.map((row, index) => (
									<ListItem
										key={index}
										secondaryAction={
											<Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
												<TextField
													label={t("approve.rows.partial.title")}
													value={row.amount}
													disabled={true}
													placeholder={t("approve.rows.partial.placeholder")}
													InputProps={{
														endAdornment: (
															<InputAdornment sx={{ mr: -0.5 }} position="end">
																<WatchLaterIcon />
															</InputAdornment>
														),
													}}
													size="small"
													sx={{
														width: "100px",
													}}
												/>
												{overtimes.length > 1 && (
													<>
														<IconButton
															size="small"
															onClick={() => {
																setSelectedEmployeeId(row.id);
																setOpenCommentDialog(true);
															}}
														>
															<ModeCommentIcon />
														</IconButton>
														<IconButton
															size="small"
															onClick={() => {
																setSelectedEmployeeId(row.id);
																setOpenNoteDialog(true);
															}}
														>
															<StickyNote2Icon />
														</IconButton>
													</>
												)}
											</Box>
										}
									>
										<ListItemText primary={`${row.date} - ${row.full_name}`} />
									</ListItem>
								))}
							</List>
						)}
					</Typography>

					{overtimes.length === 1 && (
						<>
							<Divider sx={{ my: 2 }} />

							<FormControl fullWidth size="small" sx={{ mb: 2 }}>
							<InputLabel>{t("approve.comment.label")}</InputLabel>
							<Select
								value={employeeComments[overtimes[0]?.id] || ""}
								label={t("approve.comment.label")}
								disabled={loadingComments}
								onChange={(e) => setEmployeeComments({ ...employeeComments, [overtimes[0]?.id]: e.target.value })}
							>
								<MenuItem value="">
									<em>{t("approve.comment.placeholder")}</em>
									</MenuItem>
									{comments.map((commentOption) => (
										<MenuItem key={commentOption} value={commentOption}>
											{commentOption}
										</MenuItem>
									))}
								</Select>
							</FormControl>

							<TextField
								fullWidth
								label="Nota"
								placeholder="Escribir nota"
								multiline
								rows={4}
								value={employeeNotes[overtimes[0]?.id] || ""}
								onChange={(e) => setEmployeeNotes({ ...employeeNotes, [overtimes[0]?.id]: e.target.value })}
								size="small"
							/>
						</>
					)}
				</DialogContent>

				<DialogActions>
					<LoadingButton
						variant="contained"
						disableElevation
						onClick={() => {
							trackGaClick({
								id: "overtime.add_approve_overtime",
								gaAction: "approve_overtime",
								gaType: "full",
								extra: {
									total: overtimes.length,
								},
							});

							const overtimesWithComments = overtimes.map((overtime) => ({
								...overtime,
								comment: employeeComments[overtime.id] || "",
								notes: employeeNotes[overtime.id] || "",
							}));

							applyingOvertime(setLoading, overtimesWithComments, closing, reloading, handlerAlert);
						}}
						color="primary"
						loading={loading}
						disabled={
					data.filter((d) => checked.includes(d.id)).filter((d) => d.status === "pending").length === 0 ||
					overtimes.some((ot) => employeeComments[ot.id] && !employeeNotes[ot.id]?.trim()) ||
					(overtimeRequiresComment && overtimes.some((ot) => !employeeComments[ot.id] || !employeeNotes[ot.id]?.trim()))
				}
			>
				{t("approve.buttons.approve")}
			</LoadingButton>

			<Button
				sx={{ mt: 0.5, mr: 2 }}
				disableElevation
				color="error"
				variant="contained"
						onClick={() => {
							closing();
						}}
					>
						{t("approve.buttons.cancel")}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openCommentDialog}
				onClose={() => {
					setOpenCommentDialog(false);
					setSelectedEmployeeId(null);
				}}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{t("approve.comment.title")}</DialogTitle>
				<DialogContent>
					<FormControl fullWidth size="small" sx={{ mt: 1 }}>
						<InputLabel>{t("approve.comment.label")}</InputLabel>
						<Select
							value={employeeComments[selectedEmployeeId] || ""}
							label={t("approve.comment.label")}
							disabled={loadingComments}
							onChange={(e) => {
								setEmployeeComments({ ...employeeComments, [selectedEmployeeId]: e.target.value });
							}}
						>
							<MenuItem value="">
								<em>{t("approve.comment.placeholder")}</em>
							</MenuItem>
							{comments.map((commentOption) => (
								<MenuItem key={commentOption} value={commentOption}>
									{commentOption}
								</MenuItem>
							))}
						</Select>
					</FormControl>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setOpenCommentDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("approve.buttons.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={() => {
							setOpenCommentDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("approve.buttons.save")}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog
				open={openNoteDialog}
				onClose={() => {
					setOpenNoteDialog(false);
					setSelectedEmployeeId(null);
				}}
				maxWidth="xs"
				fullWidth
			>
				<DialogTitle>{t("approve.note.title")}</DialogTitle>
				<DialogContent>
					<TextField
						fullWidth
						label={t("approve.note.label")}
						placeholder={t("approve.note.placeholder")}
						multiline
						rows={4}
						value={employeeNotes[selectedEmployeeId] || ""}
						onChange={(e) => setEmployeeNotes({ ...employeeNotes, [selectedEmployeeId]: e.target.value })}
						size="small"
						sx={{ mt: 1 }}
					/>
				</DialogContent>
				<DialogActions>
					<Button
						onClick={() => {
							setOpenNoteDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("approve.buttons.cancel")}
					</Button>
					<Button
						variant="contained"
						onClick={() => {
							setOpenNoteDialog(false);
							setSelectedEmployeeId(null);
						}}
					>
						{t("approve.buttons.save")}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export { Approve };
